import { useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Search, Trash2 } from 'lucide-react';

export function SettingsSeoTab({ settings, update }) {
  const [generatingKeywords, setGeneratingKeywords] = useState(false);
  const [keywordDescription, setKeywordDescription] = useState('');
  const [suggestedKeywords, setSuggestedKeywords] = useState([]);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-800">
          <strong>Conseils SEO local :</strong> Incluez vos mots-clés principaux dans le titre et la description. Ajoutez votre quartier, ville et spécialités.
        </p>
      </div>

      {/* Meta tags */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Balises Meta</h3>
        <div>
          <Label className="text-sm">Titre de la page (balise title)</Label>
          <Input value={settings.meta_title || ''} onChange={e => update('meta_title', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Côté Terrasse - Restaurant Terrasse Bordeaux" />
          <p className="text-xs text-charcoal/50 mt-1">{(settings.meta_title || '').length}/60 caractères recommandés</p>
        </div>
        <div>
          <Label className="text-sm">Meta description</Label>
          <Textarea value={settings.meta_description || ''} onChange={e => update('meta_description', e.target.value)} className="border-garden-200 rounded-xl" rows={3} placeholder="Restaurant de quartier à Bordeaux..." />
          <p className="text-xs text-charcoal/50 mt-1">{(settings.meta_description || '').length}/160 caractères recommandés</p>
        </div>
        <div>
          <Label className="text-sm">Auteur</Label>
          <Input value={settings.meta_author || ''} onChange={e => update('meta_author', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Côté Terrasse" />
        </div>
        <div>
          <Label className="text-sm">Langue</Label>
          <Input value={settings.meta_language || 'fr'} onChange={e => update('meta_language', e.target.value)} className="border-garden-200 rounded-xl" placeholder="fr" />
        </div>
      </div>

      {/* Open Graph */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Open Graph (partage réseaux sociaux)</h3>
        <div>
          <Label className="text-sm">og:title</Label>
          <Input value={settings.og_title || ''} onChange={e => update('og_title', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Côté Terrasse — Restaurant à Bordeaux" />
        </div>
        <div>
          <Label className="text-sm">og:description</Label>
          <Textarea value={settings.og_description || ''} onChange={e => update('og_description', e.target.value)} className="border-garden-200 rounded-xl" rows={2} placeholder="Description affichée lors des partages..." />
        </div>
        <div>
          <Label className="text-sm">og:image (URL de l'image de partage, 1200x630px idéal)</Label>
          <Input value={settings.og_image || ''} onChange={e => update('og_image', e.target.value)} className="border-garden-200 rounded-xl" placeholder="https://..." />
        </div>
        <div>
          <Label className="text-sm">og:type</Label>
          <Input value={settings.og_type || 'restaurant'} onChange={e => update('og_type', e.target.value)} className="border-garden-200 rounded-xl" placeholder="restaurant" />
        </div>
        {settings.og_image && (
          <img src={settings.og_image} alt="Aperçu" className="w-full max-w-md rounded-lg border" />
        )}
      </div>

      {/* Keywords */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Mots-clés</h3>
        <div>
          <Label className="text-sm">Mots-clés actuels (meta keywords)</Label>
          <Textarea value={settings.meta_keywords || ''} onChange={e => update('meta_keywords', e.target.value)} className="border-garden-200 rounded-xl" rows={2} placeholder="restaurant, bordeaux, terrasse, burgers..." />
          <p className="text-xs text-charcoal/50 mt-1">Séparez les mots-clés par des virgules</p>
        </div>
        
        <div className="border-t border-garden-100 pt-4">
          <h4 className="font-medium text-garden-800 text-sm mb-3">Générateur de mots-clés</h4>
          <p className="text-xs text-charcoal/50 mb-3">Décrivez votre restaurant, vos spécialités ou votre quartier pour générer des suggestions.</p>
          <div className="flex gap-2">
            <Input value={keywordDescription} onChange={e => setKeywordDescription(e.target.value)} className="border-garden-200 rounded-xl flex-1" placeholder="Terrasse ombragée, burgers maison, quartier Bergonié..." data-testid="keyword-description" />
            <Button
              onClick={async () => {
                setGeneratingKeywords(true);
                try {
                  const res = await adminApi.generateKeywords({ description: keywordDescription });
                  setSuggestedKeywords(res.data.keywords || []);
                } catch { toast.error('Erreur génération'); }
                setGeneratingKeywords(false);
              }}
              disabled={generatingKeywords}
              className="bg-garden-800 hover:bg-garden-900 text-white rounded-full"
              data-testid="generate-keywords-btn"
            >
              <Search size={14} className="mr-1" /> {generatingKeywords ? 'Génération...' : 'Générer'}
            </Button>
          </div>
          {suggestedKeywords.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium text-garden-700">{suggestedKeywords.length} suggestions :</p>
              <div className="flex flex-wrap gap-1.5">
                {suggestedKeywords.map((kw) => {
                  const current = (settings.meta_keywords || '').split(',').map(k => k.trim().toLowerCase());
                  const isAdded = current.includes(kw.toLowerCase());
                  return (
                    <button
                      key={`kw-${kw}`}
                      onClick={() => {
                        if (isAdded) return;
                        const existing = settings.meta_keywords ? settings.meta_keywords + ', ' : '';
                        update('meta_keywords', existing + kw);
                      }}
                      disabled={isAdded}
                      className={`px-2.5 py-1 rounded-full text-xs transition-all ${isAdded ? 'bg-garden-200 text-garden-700' : 'bg-garden-50 text-garden-800 hover:bg-garden-100 border border-garden-200'}`}
                    >
                      {isAdded ? '✓ ' : '+ '}{kw}
                    </button>
                  );
                })}
              </div>
              <Button variant="outline" size="sm" onClick={() => {
                update('meta_keywords', suggestedKeywords.join(', '));
                toast.success('Tous les mots-clés ajoutés');
              }} className="rounded-full text-xs mt-2">
                Utiliser tous les mots-clés
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Favicon */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Favicon</h3>
        <div className="flex items-center gap-4">
          {settings.favicon_url ? (
            <img src={settings.favicon_url} alt="Favicon actuel" className="w-16 h-16 rounded-lg border border-garden-200 object-contain bg-white p-1" />
          ) : (
            <div className="w-16 h-16 rounded-lg border border-garden-200 bg-garden-50 flex items-center justify-center text-garden-400 text-xs text-center">
              Par défaut
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm text-charcoal/70 mb-2">Image carrée recommandée (32x32 ou 64x64 px). Formats : PNG, ICO, JPG.</p>
            <div className="flex gap-2 items-center">
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setUploadingFavicon(true);
                  try {
                    const formData = new FormData();
                    formData.append('file', file);
                    const res = await adminApi.uploadFavicon(formData);
                    update('favicon_url', res.data.url);
                    toast.success('Favicon mis à jour');
                  } catch { toast.error('Erreur upload'); }
                  setUploadingFavicon(false);
                }}
                className="text-sm"
                data-testid="favicon-upload"
              />
              {settings.favicon_url && (
                <Button variant="outline" size="sm" onClick={() => update('favicon_url', '')} className="rounded-full text-xs text-red-500">
                  <Trash2 size={12} className="mr-1" /> Supprimer
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
